/**
 * Generic SecondarySidebar Component
 * ==================================
 * 
 * Generic secondary sidebar that displays next to the main sidebar.
 * Accepts menu items as props for complete flexibility.
 * 
 * @component
 * @example
 * const menuItems = [
 *   { id: '1', label: 'Item 1', icon: '●', onClick: () => {} },
 *   { id: '2', label: 'Item 2', icon: '●', onClick: () => {} }
 * ];
 * 
 * <SecondarySidebar
 *   title="Management"
 *   menuItems={menuItems}
 *   activeItemId="1"
 * />
 */

import React from 'react';
import './SecondarySidebar.scss';

export interface SecondaryMenuItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
}

interface SecondarySidebarProps {
  /** Title displayed in header */
  title: string;
  /** Menu items to display */
  menuItems: SecondaryMenuItem[];
  /** Currently active item ID */
  activeItemId?: string;
  /** Optional custom className */
  className?: string;
}

const SecondarySidebar: React.FC<SecondarySidebarProps> = ({
  title,
  menuItems,
  activeItemId,
  className = ''
}) => {
  const handleItemClick = (item: SecondaryMenuItem, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    item.onClick();
  };

  return (
    <div className={`secondary-sidebar ${className}`}>
      {/* Header */}
      <div className="secondary-sidebar__header">
        <h3 className="secondary-sidebar__title">{title}</h3>
      </div>

      {/* Menu Items */}
      <div className="secondary-sidebar__content">
        {menuItems.map((item) => {
          const isActive = activeItemId === item.id;
          
          return (
            <button
              key={item.id}
              className={`secondary-sidebar__item ${isActive ? 'secondary-sidebar__item--active' : ''}`}
              onClick={(e) => handleItemClick(item, e)}
              title={item.label}
            >
              {item.icon && (
                <span className="secondary-sidebar__item-icon">{item.icon}</span>
              )}
              <span className="secondary-sidebar__item-label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SecondarySidebar;




